import { IconButton } from '@brightdrop/bd-ui';
import { ListItemIcon, ListItemText, Typography } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SvgIcon from '@mui/material/SvgIcon';
import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Overflow from '~/assets/icons/new/overflowh.svg?react';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';

import type { NotificationOption } from '../../utils/notification.utils';

interface NotificationActionsMenuProps {
  options: NotificationOption[];
}

const NotificationActionsMenu = ({ options }: NotificationActionsMenuProps) => {
  // Subscribe to i18n so imperative `translateText(...)` updates after locale changes.
  useTranslation(['settings']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <IconButton
        edge={'end'}
        aria-label={translateText(
          'settings:notificationList.actionMenu.actionsMenuButton'
        )}
        data-testid={'notification-actions-icon-button'}
        onClick={handleClick}
        icon={<SvgIcon component={Overflow} viewBox="0 0 16 16" />}
        variant="fill"
      />
      <Menu
        anchorEl={anchorEl}
        onClick={(e: MouseEvent<HTMLElement>) => e.stopPropagation()}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            style: {
              width: '220px',
              transform: 'translateX(-10px) translateY(30px)',
            },
          },
        }}
      >
        {options?.map((option, index) => (
          <MenuItem
            key={option.id ?? index}
            onClick={(e) => {
              if (option.onClick) {
                option.onClick(e, handleClose);
              }
            }}
          >
            <ListItemIcon
              data-testid={`notification-action-menu-item-icon-${option.id ?? index}`}
            >
              {option?.icon}
            </ListItemIcon>
            <ListItemText
              data-testid={`notification-action-menu-item-text-${option.id ?? index}`}
              primary={
                <Typography variant="body_regular">{option?.text}</Typography>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default NotificationActionsMenu;
